import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { describeError, useToast } from "../../components/toast";
import { foyerApi, type OwnerShell } from "../../http/foyer";
import { PageHeader } from "../../components/PageHeader";
import { useOptionalAuth } from "../../auth/AuthContext";
import { canActForOwners } from "../../auth/actingOwner";
import { OwnerGate } from "../../components/supplier/OwnerGate";
import type { AgreementKind, AgreementStatus } from "@shared/content-agreement";
import { useFormat } from "../../i18n/format";

/**
 * Agreements — what a supplier has signed, and what they still owe
 * (`CONTENT-19`).
 *
 * ## The document is on the page, not behind a link
 *
 * `GET /owner/agreements` returns each document's `body` with its status, and
 * this page renders it where the Accept button is. That is the server's design
 * and it is load-bearing: a supplier must be able to READ what they are
 * accepting on the screen where they accept it, and a link to a page somebody
 * can edit is exactly what the stored artefact plus checksum exists to prevent.
 *
 * ## One button per document, deliberately
 *
 * There is no "accept all". Five documents behind one click produces a record
 * saying somebody clicked — not that somebody agreed to five things. The route
 * takes a single `agreementId` for the same reason.
 *
 * ## A superseded acceptance is not an acceptance
 *
 * `supersededAcceptance` means they signed an EARLIER version of this document.
 * It renders as outstanding-with-history rather than as a tick, because the
 * terms in force are the ones they have not agreed to — and a supplier who sees
 * a tick will not look again.
 */

interface ShellContext {
  shell: OwnerShell;
}

export default function SupplierAgreementsPage() {
  const toast = useToast();
  const { t } = useTranslation(["supplier", "common"]);
  const fmt = useFormat();
  const kindLabel = (kind: AgreementKind) =>
    t(`supplierAgreementsPage.kinds.${kind}`);
  useOutletContext<ShellContext>();
  // Only the supplier's own people accept; a Nenda admin supporting them sees
  // the status but cannot sign Nenda's terms on their behalf (the server
  // refuses with `owner_session_only`).
  const canAccept = !canActForOwners(useOptionalAuth()?.user);
  const [status, setStatus] = useState<AgreementStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await foyerApi.ownerAgreements());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async (agreementId: string) => {
    setBusy(agreementId);
    setError(null);
    try {
      const next = await foyerApi.acceptAgreement(agreementId);
      setStatus(next);
      const row = next.agreements.find((r) => r.agreement.id === agreementId);
      toast.success(
        row
          ? t("supplierAgreementsPage.acceptedNamed", {
              name: kindLabel(row.agreement.kind),
            })
          : t("supplierAgreementsPage.accepted")
      );
    } catch (e) {
      // A failed accept must not replace the page: the terms stay readable.
      const row = status?.agreements.find(
        (r) => r.agreement.id === agreementId
      );
      toast.error(
        row
          ? describeError(t, "acceptAgreementNamed", e, {
              name: kindLabel(row.agreement.kind),
            })
          : describeError(t, "acceptAgreement", e)
      );
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!status)
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("supplierAgreementsPage.eyebrow")}
        title={t("supplierAgreementsPage.title")}
        subtitle={t("supplierAgreementsPage.subtitle")}
      />

      {status.agreements.length === 0 ? (
        <Box sx={{ py: 5, textAlign: "center" }}>
          <Typography sx={{ typography: "titleLg", mb: 1 }}>
            {t("supplierAgreementsPage.nothingToSign")}
          </Typography>
          <Typography
            sx={{ color: tokens.inkMuted, maxWidth: 460, mx: "auto" }}
          >
            {t("supplierAgreementsPage.nothingToSignDetail")}
          </Typography>
        </Box>
      ) : status.satisfied ? (
        <Alert severity="success">{t("supplierAgreementsPage.upToDate")}</Alert>
      ) : (
        /* 🚨 The count is the headline. An outstanding document is the thing
           that stops material going live, and a supplier who has to count the
           rows themselves will not. */
        <Alert severity="warning" data-testid="agreements-outstanding">
          {t("supplierAgreementsPage.outstanding", {
            count: status.outstanding.length,
          })}
        </Alert>
      )}

      <Stack spacing={2}>
        {status.agreements.map((row) => {
          const accepted = row.acceptedAt !== null && !row.supersededAcceptance;
          return (
            <Box
              key={row.agreement.id}
              sx={{
                background: tokens.surface,
                border: `1px solid ${tokens.divider}`,
                borderRadius: 2,
                p: 2.5,
              }}
              data-testid={`agreement-${row.agreement.kind}`}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
                    {kindLabel(row.agreement.kind)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
                    {t("supplierAgreementsPage.versionInForce", {
                      version: row.agreement.version,
                      date: fmt.date(row.agreement.effectiveFrom),
                    })}
                  </Typography>
                </Box>
                {accepted ? (
                  <Chip
                    size="small"
                    color="primary"
                    label={t("supplierAgreementsPage.acceptedOn", {
                      date: fmt.date(row.acceptedAt!),
                    })}
                  />
                ) : !canAccept ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t("supplierAgreementsPage.ownerOnly")}
                    data-testid={`agreement-pending-${row.agreement.kind}`}
                  />
                ) : (
                  <OwnerGate
                    resource="content-owners"
                    level="edit"
                    deniedProps={{ disabled: true }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      disabled={busy !== null}
                      onClick={() => void accept(row.agreement.id)}
                      data-testid={`agreement-accept-${row.agreement.kind}`}
                    >
                      {t("supplierAgreementsPage.accept")}
                    </Button>
                  </OwnerGate>
                )}
              </Stack>

              {/* 🚨 Superseded is NOT accepted. They agreed to an older text,
                  and the terms in force are ones they have not seen. Shown as
                  history under an Accept button, never as a tick. */}
              {row.supersededAcceptance ? (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  {t("supplierAgreementsPage.superseded")}
                </Alert>
              ) : null}

              <Box
                sx={{
                  maxHeight: 260,
                  overflowY: "auto",
                  background: tokens.canvas,
                  border: `1px solid ${tokens.divider}`,
                  borderRadius: 1,
                  p: 2,
                  whiteSpace: "pre-wrap",
                  typography: "bodySm",
                  color: tokens.inkMuted,
                }}
                data-testid={`agreement-body-${row.agreement.kind}`}
              >
                {row.agreement.body}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
